import React, { ChangeEvent, FC, FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AgreementCheckbox } from 'components/AgreementCheckbox';
import { useCountryContext } from 'context/CountryProvider';
import { COMMENT_LENGTH, NUMBER_REGEXP, PHONE_LENGTH } from 'common/config';
import { useService } from 'common/hooks/useService';
import { getComplainAgreementItems } from 'common/helpers/complain.helper';
import { IComplainFormData } from 'common/types/complain.types';

interface IComponentProps {
  organizationId?: number;
  organizationName?: string | null;
  uuid?: string;
}

export const Complain: FC<IComponentProps> = ({ organizationId, organizationName, uuid }) => {
  // Официант жалобы - текущий выбранный (prop uuid в момент отправки), не снимок при первом показе формы:
  // выбор другого официанта и очистка после отправки привязку не теряют (решение владельца 2026-10-30)
  const [formData, setFormData] = useState<IComplainFormData>({ text_comment: '' });
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const { Service } = useService();
  const { country } = useCountryContext();

  const agreementsIsVisible = formData.name?.length || formData.phone?.length;
  const submitDisabled = agreementsIsVisible
    ? !formData.terms_and_conditions || !formData.text_comment.length
    : !formData.text_comment.length;

  const agreementItems = getComplainAgreementItems(t, formData, country);

  const onCommentChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, text_comment: e.target.value }));
  };

  const onNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, name: e.target.value }));
  };

  const onPhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value.length <= PHONE_LENGTH) {
      setFormData((prev) => ({ ...prev, phone: value }));
    }
  };

  const onAgreementChange = (name: string, value: boolean) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { text_comment, name, phone } = formData;

    if (organizationId) {
      setSubmitLoading(true);

      await Service.submitComplainForm({
        organization_id: organizationId,
        text_comment,
        name,
        phone,
        uuid,
        onSuccess: () => {
          setFormData({
            text_comment: '',
            name: '',
            phone: '',
            terms_and_conditions: false,
          });
        },
      });

      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    if (!agreementsIsVisible) {
      setFormData((prev) => ({ ...prev, terms_and_conditions: false }));
    }
  }, [agreementsIsVisible]);

  return (
    <form className="complain" onSubmit={onSubmit}>
      <div className="complain__suggestion">
        <div className="complain__suggestion-title">{`${t('payment.complain.whatWeCanImprove')} ${organizationName || ''}?`}</div>

        <textarea
          value={formData.text_comment}
          onChange={onCommentChange}
          className="input"
          name="text_comment"
          placeholder={t('payment.complain.commentPlaceholder')}
          rows={3}
          maxLength={COMMENT_LENGTH}
        />
      </div>

      <div className="complain__contacts">
        <div className="complain__contacts-description">{t('payment.complain.contactsDescription')}</div>

        <input
          className="input"
          value={formData.name}
          onChange={onNameChange}
          name="name"
          placeholder={t('payment.complain.name')}
        />

        <input
          name="phone"
          value={formData.phone}
          onChange={onPhoneChange}
          className="input"
          type="number"
          placeholder={t('payment.complain.phone')}
          onKeyDown={(e) => {
            const regexp = NUMBER_REGEXP;

            if (!regexp.test(e.key) && e.key !== 'Backspace') {
              e.preventDefault();
            }
          }}
        />
      </div>

      <div className="btn btn-submit">
        <button disabled={submitDisabled || submitLoading}>{t('payment.complain.leaveFeedback')}</button>
      </div>

      <div className={`complain__agreements ${agreementsIsVisible ? 'visible' : ''}`}>
        {agreementItems.map(({ label, name, checked }, index) => {
          return <AgreementCheckbox key={index} label={label} name={name} checked={checked} onChange={onAgreementChange} />;
        })}
      </div>
    </form>
  );
};
